import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { isAbortError } from '@theme/utilities';

/** @type {Record<string, string>} */
const sharedRecommendationsCache = {};

/** @type {Map<string, Promise<{ success: true, data: string } | { success: false, status: number }>>} */
const recommendationsInFlightRequests = new Map();

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // Loading-state attributes should not retrigger a fetch
      if (mutation.attributeName === 'aria-busy' || mutation.attributeName === 'aria-label') continue;

      this.#loadRecommendations();
      break;
    }
  });

  /** @type {Promise<void> | null} */
  #loadPromise = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load recommendations on viewport intersection or observed attribute changes.
   * @returns {Promise<void>}
   */
  #loadRecommendations() {
    if (this.#loadPromise) {
      return this.#loadPromise;
    }

    this.#loadPromise = (async () => {
      const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
      const id = this.id;

      if (!productId || !id) {
        this.#handleError(new Error('Product ID and an ID attribute are required'));
        return;
      }

      // If the recommendations have already been loaded, accounts for the case where the Theme Editor
      // is loaded the section from the editor's visual preview context.
      if (recommendationsPerformed === 'true') {
        return;
      }

      try {
        const result = await this.#fetchCachedRecommendations(productId, sectionId, intent);

        if (!result.success) {
          // The Theme Editor will place a section element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          } else {
            this.#clearBusyState();
          }
          return;
        }

        const fragment = new DOMParser().parseFromString(result.data, 'text/html');
        const recommendations = fragment.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations instanceof HTMLElement && recommendations.firstElementChild) {
          this.dataset.recommendationsPerformed = 'true';
          this.classList.remove('hidden');
          delete this.dataset.error;
          this.#clearBusyState();
          morph(this, recommendations, MORPH_OPTIONS);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      } catch (error) {
        if (isAbortError(error)) {
          this.#clearBusyState();
          return;
        }

        this.#handleError(error instanceof Error ? error : new Error('Product recommendations error'));
      }
    })().finally(() => {
      this.#loadPromise = null;
    });

    return this.#loadPromise;
  }

  /**
   * Fetches the recommendations and caches the result for future use across all instances.
   * credentials: 'omit' matches `<link rel="preload" as="fetch" crossorigin>` so the preload is reused.
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = sharedRecommendationsCache[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    let inFlightRequest = recommendationsInFlightRequests.get(url);
    if (!inFlightRequest) {
      inFlightRequest = (async () => {
        const response = await fetch(url, { credentials: 'omit' });
        if (!response.ok) {
          return { success: false, status: response.status };
        }

        const text = await response.text();
        sharedRecommendationsCache[url] = text;
        return { success: true, data: text };
      })();
      recommendationsInFlightRequests.set(url, inFlightRequest);
    }

    try {
      return await inFlightRequest;
    } finally {
      if (recommendationsInFlightRequests.get(url) === inFlightRequest) {
        recommendationsInFlightRequests.delete(url);
      }
    }
  }

  #clearBusyState() {
    this.removeAttribute('aria-busy');
    this.removeAttribute('aria-label');
  }

  /**
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.#clearBusyState();
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
